import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaMapMarkerAlt, FaSave, FaTimes, FaSignOutAlt, FaInfoCircle } from 'react-icons/fa';
import { initGoogleMaps } from '../../utils/googleMaps';
import { addToilet } from '../../utils/toiletService';
import './AddToilet.css';

// Security and validation utilities
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove potentially dangerous characters and patterns
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove < and > to prevent HTML injection
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/script/gi, '') // Remove script tags
    .replace(/sql/gi, '') // Remove SQL keywords
    .replace(/union/gi, '') // Remove SQL UNION
    .replace(/select/gi, '') // Remove SQL SELECT
    .replace(/insert/gi, '') // Remove SQL INSERT
    .replace(/update/gi, '') // Remove SQL UPDATE
    .replace(/delete/gi, '') // Remove SQL DELETE
    .replace(/drop/gi, '') // Remove SQL DROP
    .replace(/create/gi, '') // Remove SQL CREATE
    .replace(/alter/gi, '') // Remove SQL ALTER
    .replace(/exec/gi, '') // Remove SQL EXEC
    .replace(/execute/gi, '') // Remove SQL EXECUTE
    .replace(/xp_/gi, '') // Remove SQL extended procedures
    .replace(/sp_/gi, '') // Remove SQL stored procedures
    .replace(/--/g, '') // Remove SQL comments
    .replace(/\/\*/g, '') // Remove SQL block comments
    .replace(/\*\//g, '') // Remove SQL block comments
    .replace(/;/g, '') // Remove semicolons
    .replace(/'/g, "''") // Escape single quotes for SQL
    .replace(/"/g, '""') // Escape double quotes for SQL
    .replace(/\\/g, '\\\\') // Escape backslashes
    .replace(/\$/g, '\\$') // Escape dollar signs
    .replace(/\{/g, '\\{') // Escape curly braces
    .replace(/\}/g, '\\}') // Escape curly braces
    .replace(/\[/g, '\\[') // Escape square brackets
    .replace(/\]/g, '\\]') // Escape square brackets
    .replace(/\(/g, '\\(') // Escape parentheses
    .replace(/\)/g, '\\)') // Escape parentheses
    .replace(/\|/g, '\\|') // Escape pipes
    .replace(/\^/g, '\\^') // Escape carets
    .replace(/\*/g, '\\*') // Escape asterisks
    .replace(/\+/g, '\\+') // Escape plus signs
    .replace(/\?/g, '\\?') // Escape question marks
    .replace(/\./g, '\\.') // Escape dots
    .replace(/\//g, '\\/') // Escape forward slashes
    .replace(/\\\\/g, '\\\\\\\\'); // Double escape backslashes
};

const validateInput = (input, fieldName, maxLength = 255) => {
  if (!input || typeof input !== 'string') {
    throw new Error(`${fieldName} is required`);
  }

  const sanitized = sanitizeInput(input);
  
  if (sanitized.length === 0) {
    throw new Error(`${fieldName} cannot be empty`);
  }

  if (sanitized.length > maxLength) {
    throw new Error(`${fieldName} must be ${maxLength} characters or less`);
  }

  // Additional field-specific validation
  switch (fieldName.toLowerCase()) {
    case 'toilet name':
      if (!/^[a-zA-Z0-9\s\-_.,&()]+$/i.test(sanitized)) {
        throw new Error('Toilet name contains invalid characters');
      }
      if (sanitized.length < 2) {
        throw new Error('Toilet name must be at least 2 characters long');
      }
      break;
    
    case 'description':
      if (!/^[a-zA-Z0-9\s\-_.,!?()&@#$%]+$/i.test(sanitized)) {
        throw new Error('Description contains invalid characters');
      }
      break;
    
    case 'payment details':
      if (!/^[a-zA-Z0-9\s\-_.,£$€¥()]+$/i.test(sanitized)) {
        throw new Error('Payment details contain invalid characters');
      }
      break;
    
    case 'address':
      if (!/^[a-zA-Z0-9\s\-_,.()]+$/i.test(sanitized)) {
        throw new Error('Address contains invalid characters');
      }
      break;
    default:
      break;
  }

  return sanitized;
};

const AddToilet = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const addressInputRef = useRef(null);
  const addressAutocompleteRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showOpeningHoursTooltip, setShowOpeningHoursTooltip] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    is_public: true,
    is_free: true,
    is_accessible: false,
    is_men_only: false,
    is_women_only: false,
    is_gender_neutral: false,
    has_baby_changing: false,
    radar: false,
    cleanliness_rating: 3.0,
    description: '',
    payment_details: '',
    opening_hours: {
      monday: { open: '', close: '', closed: false },
      tuesday: { open: '', close: '', closed: false },
      wednesday: { open: '', close: '', closed: false },
      thursday: { open: '', close: '', closed: false },
      friday: { open: '', close: '', closed: false },
      saturday: { open: '', close: '', closed: false },
      sunday: { open: '', close: '', closed: false }
    }
  });

  useEffect(() => {
    // Initialize Google Maps
    initGoogleMaps()
      .then(() => {
        
        setIsGoogleMapsLoaded(true);
        setMapsError(null);
      })
      .catch((error) => {
        console.error('Error initializing Google Maps:', error);
        setMapsError(error.message);
      });
  }, []);

  // Initialize Google Places Autocomplete for address field
  useEffect(() => {
    if (isGoogleMapsLoaded && !mapsError && window.google) {
      try {
        // Initialize Google Places Autocomplete for address input
        if (addressInputRef.current) {
          addressAutocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: 'uk' }
          });
          
          addressAutocompleteRef.current.addListener('place_changed', () => {
            const place = addressAutocompleteRef.current.getPlace();
            if (place.formatted_address) {
              // Parse the address components
              const addressComponents = {
                street_number: '',
                route: '',
                locality: '',
                administrative_area_level_1: '',
                postal_code: ''
              };

              place.address_components.forEach(component => {
                const types = component.types;
                if (types.includes('street_number')) {
                  addressComponents.street_number = component.long_name;
                } else if (types.includes('route')) {
                  addressComponents.route = component.long_name;
                } else if (types.includes('locality')) {
                  addressComponents.locality = component.long_name;
                } else if (types.includes('administrative_area_level_1')) {
                  addressComponents.administrative_area_level_1 = component.long_name;
                } else if (types.includes('postal_code')) {
                  addressComponents.postal_code = component.long_name;
                }
              });

              // Update form data with parsed address (include city and postcode in address)
              const fullAddress = `${addressComponents.street_number} ${addressComponents.route}`.trim();
              const cityPostcode = `${addressComponents.locality ? addressComponents.locality + ', ' : ''}${addressComponents.postal_code || ''}`.trim();
              const completeAddress = cityPostcode ? `${fullAddress}, ${cityPostcode}` : fullAddress;
              
              setFormData(prev => ({
                ...prev,
                address: completeAddress
              }));

            }
          });
        }
      } catch (error) {
        console.error('Error initializing Places Autocomplete:', error);
        setMapsError('Failed to initialize location search');
      }
    }
  }, [isGoogleMapsLoaded, mapsError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Real-time validation for text fields
    if (type === 'text' || type === 'textarea') {
      try {
        if (value.trim()) {
          switch (name) {
            case 'name':
              validateInput(value, 'Toilet Name', 255);
              break;
            case 'description':
              if (value.trim()) {
                validateInput(value, 'Description', 1000);
              }
              break;
            case 'payment_details':
              if (value.trim()) {
                validateInput(value, 'Payment Details', 255);
              }
              break;
            case 'address':
              validateInput(value, 'Address', 500);
              break;
            default:
              break;
          }
        }
      } catch (error) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: error.message
        }));
      }
    }
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value
        }
      }
    }));
  };

  const getCoordinatesFromAddress = async (address) => {
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API not loaded');
    }

    const geocoder = new window.google.maps.Geocoder();
    
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            latitude: location.lat(),
            longitude: location.lng()
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Validate and sanitize all text inputs
      const sanitizedName = validateInput(formData.name, 'Toilet Name', 255);
      const sanitizedAddress = validateInput(formData.address, 'Address', 500);
      const sanitizedDescription = formData.description ? validateInput(formData.description, 'Description', 1000) : '';
      const sanitizedPaymentDetails = formData.payment_details ? validateInput(formData.payment_details, 'Payment Details', 255) : '';


      // Get coordinates from address using Google Maps Geocoding
      const coordinates = await getCoordinatesFromAddress(sanitizedAddress);
      

      // Prepare areas JSON
      const areas = {
        address: sanitizedAddress
      };

      // Prepare opening hours for database
      const openingHoursData = [];
      const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      daysOfWeek.forEach((day, index) => {
        const dayData = formData.opening_hours[day];
        if (!dayData.closed && dayData.open && dayData.close) {
          openingHoursData.push({
            dow: index,
            open_time: dayData.open,
            close_time: dayData.close
          });
        }
      });

      // Create toilet data with sanitized inputs
      const toiletData = {
        name: sanitizedName,
        areas: areas,
        location: `POINT(${coordinates.longitude} ${coordinates.latitude})`,
        is_public: formData.is_public,
        is_free: formData.is_free,
        is_accessible: formData.is_accessible,
        is_men_only: formData.is_men_only,
        is_women_only: formData.is_women_only,
        is_gender_neutral: formData.is_gender_neutral,
        has_baby_changing: formData.has_baby_changing,
        radar: formData.radar,
        cleanliness_rating: parseFloat(formData.cleanliness_rating),
        description: sanitizedDescription,
        payment_details: sanitizedPaymentDetails
      };

      // Call the API to insert toilet
      const result = await addToilet(toiletData, openingHoursData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setSubmitSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Minimal inline style placeholders (used in markup)
  const labelStyle = { display: 'block' };
  const inputStyle = { width: '100%' };
  const checkboxContainerStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem' };
  const checkboxStyle = { width: '1rem', height: '1rem' };
  const errorStyle = { position: 'absolute' };
  const validationErrorStyle = { position: 'absolute' };
  // Match Home.js infoTooltipStyle: hidden by default, visible on hover/toggle
  const openingHoursTooltipStyle = {
    position: 'absolute',
    backgroundColor: '#fff',
    border: '2px solid #004C10',
    borderRadius: '8px',
    padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1003,
    width: window.innerWidth <= 768 ? '200px' : '250px',
    fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
    color: '#004C10',
    fontWeight: '500',
    top: window.innerWidth <= 768 ? '100%' : '50%',
    left: window.innerWidth <= 768 ? '50%' : '100%',
    marginTop: window.innerWidth <= 768 ? '0.5rem' : '0',
    marginLeft: window.innerWidth <= 768 ? '0' : '0.5rem',
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: window.innerWidth <= 768 ? 'translateX(-50%) translateY(-10px)' : 'translateY(-50%) translateX(-10px)'
  };
  const openingHoursTooltipVisibleStyle = {
    ...openingHoursTooltipStyle,
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
    transform: window.innerWidth <= 768 ? 'translateX(-50%) translateY(0)' : 'translateY(-50%) translateX(0)'
  };

  if (!user) {
    return (
      <div className="add-toilet-container">
        <div className="add-toilet-form">
          <h2 style={{ color: '#004C10', textAlign: 'center' }}>
            Please log in to add toilets
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="add-toilet-container">
      
      {/* Mobile Toggle Button - Only visible on mobile */}
      {window.innerWidth <= 768 && user && (
        <button
          onClick={() => setIsMobilePanelOpen(!isMobilePanelOpen)}
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1003,
            backgroundColor: '#004C10',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '3rem',
            height: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          {isMobilePanelOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Mobile Backdrop - Only visible when panel is open */}
      {window.innerWidth <= 768 && isMobilePanelOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1001,
          }}
          onClick={() => setIsMobilePanelOpen(false)}
        />
      )}

      {/* Mobile Navigation Panel - Only visible on mobile */}
      {window.innerWidth <= 768 && user && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: isMobilePanelOpen ? '0' : '-85%',
          width: '85%',
          height: '100vh',
          backgroundColor: '#fff',
          zIndex: 1004,
          transition: 'right 0.3s ease',
          overflowY: 'auto',
          boxShadow: isMobilePanelOpen ? '0 0 20px rgba(0,0,0,0.3)' : 'none',
        }}>
          <div style={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}>
            {/* Close Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}>
              <h2 style={{
                color: '#004C10',
                fontSize: '1.2rem',
                fontFamily: '"Zen Old Mincho", serif',
                margin: 0,
              }}>
                Navigation
              </h2>
              <button
                onClick={() => setIsMobilePanelOpen(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#004C10',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}>
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#004C10',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: '"Zen Old Mincho", serif',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => setIsMobilePanelOpen(false)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#006622';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#004C10';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaMapMarkerAlt size={16} />
                Back to Search
              </Link>
              
              <button
                onClick={() => {
                  logout();
                  setIsMobilePanelOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#004C10',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: '"Zen Old Mincho", serif',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#006622';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#004C10';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaSignOutAlt size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="add-toilet-form">
        <h1 style={{ 
          color: '#004C10', 
          textAlign: 'center', 
          marginBottom: window.innerWidth <= 768 ? '1rem' : '2rem',
          fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem'
        }}>
          Add New Toilet
        </h1>

        {submitError && (
          <div style={{
            position: 'relative',
            marginBottom: '1rem'
          }}>
            <div style={errorStyle}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '-8px',
                transform: 'translateY(-50%)',
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderRight: '8px solid #ff0000',
              }}></div>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '-6px',
                transform: 'translateY(-50%)',
                borderTop: '7px solid transparent',
                borderBottom: '7px solid transparent',
                borderRight: '7px solid #fff',
              }}></div>
              {submitError}
            </div>
          </div>
        )}
        {submitSuccess && (
          <div className="add-toilet-success">
            Toilet added successfully! Redirecting to home page...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="add-toilet-section">
            <h3 style={{ 
              color: '#004C10', 
              marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
              fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem'
            }}>Basic Information</h3>
            
            <div style={{ 
              marginBottom: '1rem',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              <label className="add-toilet-label">Toilet Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="add-toilet-input"
                style={{
                  borderColor: validationErrors.name ? '#dc3545' : '#ccc'
                }}
                placeholder="e.g., Central Park Public Toilet"
                required
              />
              {validationErrors.name && (
                <div style={validationErrorStyle}>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                  {validationErrors.name}
                </div>
              )}
            </div>

            <div style={{ 
              marginBottom: '1rem',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              <label className="add-toilet-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="add-toilet-input"
                style={{
                  minHeight: '100px',
                  resize: 'vertical',
                  borderColor: validationErrors.description ? '#dc3545' : '#ccc'
                }}
                placeholder="Additional details about the toilet..."
              />
              {validationErrors.description && (
                <div style={validationErrorStyle}>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                  {validationErrors.description}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="add-toilet-section">
            <h3 style={{ 
              color: '#004C10', 
              marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
              fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem'
            }}>Location</h3>
            
            <div style={{ 
              marginBottom: '1rem',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              <label className="add-toilet-label">Address *</label>
              <input
                ref={addressInputRef}
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="add-toilet-input"
                style={{
                  borderColor: validationErrors.address ? '#dc3545' : '#ccc'
                }}
                placeholder="Start typing to search for an address..."
                required
              />
              {validationErrors.address && (
                <div style={validationErrorStyle}>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                  {validationErrors.address}
                </div>
              )}
            </div>
          </div>

          {/* Access Settings */}
          <div className="add-toilet-section">
            <h3 style={{ 
              color: '#004C10', 
              marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
              fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem'
            }}>Access Settings</h3>
            
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              gap: window.innerWidth <= 768 ? '0.75rem' : '2rem',
              alignItems: 'flex-start'
            }}>
              {/* Left Column - Main Access Settings */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                flex: 1
              }}>
                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Public Access</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_free"
                    checked={formData.is_free}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Free to use</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_accessible"
                    checked={formData.is_accessible}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Wheelchair accessible</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="radar"
                    checked={formData.radar}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Radar key required</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="has_baby_changing"
                    checked={formData.has_baby_changing}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Baby changing facilities</label>
                </div>
              </div>

              {/* Right Column - Gender Access Settings */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                flex: 1
              }}>
                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_men_only"
                    checked={formData.is_men_only}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Men only</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_women_only"
                    checked={formData.is_women_only}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Women only</label>
                </div>

                <div style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    name="is_gender_neutral"
                    checked={formData.is_gender_neutral}
                    onChange={handleInputChange}
                    style={checkboxStyle}
                  />
                  <label style={labelStyle}>Gender neutral</label>
                </div>
              </div>
            </div>
          </div>

          {/* Ratings and Payment */}
          <div className="add-toilet-section">
            <h3 style={{ 
              color: '#004C10', 
              marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
              fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem'
            }}>Ratings and Payment</h3>
            
            <div style={{ 
              marginBottom: '1rem',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <label style={labelStyle}>Cleanliness Rating (0-5)</label>
              <input
                type="number"
                name="cleanliness_rating"
                value={formData.cleanliness_rating}
                onChange={handleInputChange}
                className="add-toilet-input"
                min="0"
                max="5"
                step="0.1"
              />
            </div>

            <div style={{ 
              marginBottom: '1rem',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              <label style={labelStyle}>Payment Details</label>
              <input
                type="text"
                name="payment_details"
                value={formData.payment_details}
                onChange={handleInputChange}
                className="add-toilet-input"
                style={{
                  borderColor: validationErrors.payment_details ? '#dc3545' : '#ccc'
                }}
                placeholder="e.g., £0.50, Free, Contactless payment"
              />
              {validationErrors.payment_details && (
                <div style={validationErrorStyle}>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                  {validationErrors.payment_details}
                </div>
              )}
            </div>
          </div>

          {/* Opening Hours */}
          <div className="add-toilet-section">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
              position: 'relative'
            }}>
              <h3 style={{ 
                color: '#004C10', 
                fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem',
                margin: 0
              }}>Opening Hours</h3>
              <FaInfoCircle
                style={{
                  color: '#004C10',
                  fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem',
                  cursor: 'pointer',
                  opacity: 0.7,
                  transition: 'opacity 0.3s ease'
                }}
                onMouseEnter={() => setShowOpeningHoursTooltip(true)}
                onMouseLeave={() => setShowOpeningHoursTooltip(false)}
                onTouchStart={() => setShowOpeningHoursTooltip(true)}
                onTouchEnd={() => setTimeout(() => setShowOpeningHoursTooltip(false), 2000)}
              />
              <div style={showOpeningHoursTooltip ? openingHoursTooltipVisibleStyle : openingHoursTooltipStyle}>
                {window.innerWidth <= 768 ? (
                  // Mobile arrow - pointing up
                  <>
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderTop: '8px solid #004C10',
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderLeft: '7px solid transparent',
                      borderRight: '7px solid transparent',
                      borderTop: '7px solid #fff',
                      marginTop: '1px',
                    }}></div>
                  </>
                ) : (
                  // Desktop arrow - pointing left
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-8px',
                      transform: 'translateY(-50%)',
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderRight: '8px solid #004C10',
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-6px',
                      transform: 'translateY(-50%)',
                      borderTop: '7px solid transparent',
                      borderBottom: '7px solid transparent',
                      borderRight: '7px solid #fff',
                    }}></div>
                  </>
                )}
                Opening hours are optional. You can mark days as "Closed" or leave them blank if you're unsure of the exact times.
              </div>
            </div>
            
            <div className="add-toilet-opening-hours-grid">
              {Object.entries(formData.opening_hours).map(([day, hours]) => (
                                  <div key={day} className="add-toilet-day">
                  <h4 style={{ color: '#004C10', marginBottom: '0.5rem', textTransform: 'capitalize' }}>
                    {day}
                  </h4>
                  
                  <div style={checkboxContainerStyle}>
                    <input
                      type="checkbox"
                      checked={hours.closed}
                      onChange={(e) => handleOpeningHoursChange(day, 'closed', e.target.checked)}
                      style={checkboxStyle}
                    />
                    <label>Closed</label>
                  </div>

                  {!hours.closed && (
                                      <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', 
                    gap: window.innerWidth <= 768 ? '0.25rem' : '0.5rem' 
                  }}>
                    <div>
                      <label style={{ fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem' }}>Open</label>
                                               <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => handleOpeningHoursChange(day, 'open', e.target.value)}
                          style={{ 
                            ...inputStyle, 
                            padding: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                            width: '90%',
                            maxWidth: window.innerWidth <= 768 ? '140px' : '150px',
                            boxSizing: 'border-box',
                            fontSize: window.innerWidth <= 768 ? '0.5rem' : '0.8rem'
                          }}
                        />
                    </div>
                    <div>
                      <label style={{ fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem' }}>Close</label>
                                               <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => handleOpeningHoursChange(day, 'close', e.target.value)}
                          style={{ 
                            ...inputStyle, 
                            padding: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                            width: '90%',
                            maxWidth: window.innerWidth <= 768 ? '140px' : '180px',
                            boxSizing: 'border-box',
                            fontSize: window.innerWidth <= 768 ? '0.5rem' : '0.8rem'
                          }}
                        />
                    </div>
                  </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: window.innerWidth <= 768 ? '0.5rem' : '1rem', 
            marginTop: window.innerWidth <= 768 ? '1rem' : '2rem',
            flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
          }}>
            <button
              type="submit"
              disabled={isSubmitting}
              className="add-toilet-button"
            >
              <FaSave />
              {isSubmitting ? 'Adding...' : 'Add Toilet'}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/')}
              className="add-toilet-cancel-button"
            >
              <FaTimes />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddToilet;
